import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import { ShieldCheck, ShieldX } from "lucide-react";
import dayjs from "dayjs";

// Phase 17-D item 8: public verification page for evidence-pack codes.
// Shows the minimum: first name + initial, hours, range, orgs.
export default function VerifyEvidence() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setData(null); setInvalid(false);
    api.verifyEvidenceCode(code).then(setData).catch(() => setInvalid(true));
  }, [code]);

  return (
    <div className="bg-cream min-h-screen" data-testid="verify-page">
      <Seo title="Verify evidence" description="Check the authenticity of a CivicHearth volunteering evidence pack." />
      <div className="max-w-xl mx-auto px-6 py-20">
        <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">Evidence verification</div>
        <h1 className="font-display text-3xl sm:text-4xl text-forest mt-2 tracking-tight">Is this pack genuine?</h1>

        {!data && !invalid && <div className="mt-8 text-neutral-500">Checking code…</div>}

        {invalid && (
          <div data-testid="verify-invalid" className="mt-8 bg-white border border-red-200 rounded-2xl p-8 text-center">
            <ShieldX className="w-9 h-9 text-red-400 mx-auto" />
            <div className="font-display text-2xl text-forest mt-3">We don't recognise that code</div>
            <p className="text-sm text-neutral-600 mt-2">
              <span className="font-mono">{code}</span> doesn't match any evidence pack. Check for typos —
              codes look like <span className="font-mono">CH-XXXXXXXXXXXXXXXX</span>.
            </p>
          </div>
        )}

        {data && (
          <div data-testid="verify-valid" className="mt-8 bg-white border border-forest/20 rounded-2xl p-8">
            <div className="flex items-center gap-2.5 text-forest">
              <ShieldCheck className="w-7 h-7 text-green-600" />
              <div className="font-display text-2xl">Verified by CivicHearth</div>
            </div>
            <div className="mt-6 space-y-3 text-sm">
              <Row label="Volunteer" value={data.volunteer} testId="verify-volunteer" />
              <Row label="Approved hours" value={`${data.total_hours}h`} testId="verify-hours" />
              <Row label="Period" value={`${dayjs(data.from_date).format("D MMM YYYY")} — ${dayjs(data.to_date).format("D MMM YYYY")}`} testId="verify-range" />
              <Row label="Issuing organisations" value={data.orgs.join(", ")} testId="verify-orgs" />
              <Row label="Pack issued" value={dayjs(data.issued_at).format("D MMM YYYY")} testId="verify-issued" />
            </div>
            <p className="text-[11px] text-neutral-500 mt-6">
              Hours are logged by volunteers and approved by the organisations named above.
              Questions? <a href="mailto:hello@civichearth.com" className="underline">hello@civichearth.com</a>
            </p>
          </div>
        )}

        <p className="text-xs text-neutral-500 mt-6">
          CivicHearth is where neighbours volunteer, ask for help and organise locally.{" "}
          <Link to="/" className="underline text-forest">Find out more</Link>
        </p>
      </div>
    </div>
  );
}

function Row({ label, value, testId }) {
  return (
    <div className="flex items-start justify-between gap-6 border-t border-neutral-100 pt-3" data-testid={testId}>
      <span className="text-neutral-500">{label}</span>
      <span className="font-medium text-forest text-right">{value}</span>
    </div>
  );
}
